import React from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, CheckCircle2, Euro, Gift } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./RegisterOssServicesPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const SERVICES_STORAGE_KEY = "legup_oss_services";
const COMPANY_DETAILS_STORAGE_KEY = "legup_oss_company_details";

function normalise(value = "") {
  return value.toLowerCase().trim();
}

function getServiceTitle(service, language) {
  return (
    service[`label_${language}`] ||
    service.label_en ||
    service.label ||
    service.code
  );
}

function getServiceDescription(service, language) {
  return (
    service[`description_${language}`] ||
    service.description_en ||
    service.description ||
    ""
  );
}

function createDefaultSelection(service, language = "en") {
  return {
    service_code: service.code,
    label: getServiceTitle(service, language),
    description: getServiceDescription(service, language),
    service_type: "",
    subsidy_status: "NOT_APPLICABLE",
    approximate_cost_text: "",
    subsidy_information_url: "",
    subsidy_information_label: "",
    subsidy_notes: "",
  };
}

function formatEuroNumber(value = "") {
  const clean = String(value).replace(/[^\d.]/g, "");

  if (!clean) return "";

  const [integerPart, decimalPart] = clean.split(".");
  const formattedInteger = Number(integerPart || 0).toLocaleString("en-US");

  if (decimalPart !== undefined) {
    return `${formattedInteger}.${decimalPart.slice(0, 2)}`;
  }

  return formattedInteger;
}

function normaliseEuroInput(value = "") {
  return formatEuroNumber(value);
}

export default function RegisterOssServicesPage() {
  const { t, i18n } = useTranslation("oss-registration-services");
  const navigate = useNavigate();

  const language = React.useMemo(() => {
    const lang = i18n.resolvedLanguage || i18n.language || "en";
    const base = lang.split("-")[0];

    if (["en", "es", "ca", "nl"].includes(base)) return base;
    return "en";
  }, [i18n.language, i18n.resolvedLanguage]);

  const [services, setServices] = React.useState([]);
  const [selections, setSelections] = React.useState(() => {
    try {
      const saved = localStorage.getItem(SERVICES_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({});

  React.useEffect(() => {
    async function loadServices() {
      setLoading(true);
      setErr("");

      try {
        const res = await fetch(`${API_BASE}/api/oss/service-catalog/`);
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setErr(data?.detail || t("messages.catalogFailed"));
          return;
        }

        const items = Array.isArray(data) ? data : data.results || [];
        setServices(items);
      } catch (ex) {
        setErr(ex?.message || String(ex));
      } finally {
        setLoading(false);
      }
    }

    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(selections));
    } catch (error) {
      console.error("Failed to save OSS services:", error);
    }
  }, [selections]);

  const filteredServices = React.useMemo(() => {
    const term = normalise(search);

    if (!term) return services;

    return services.filter((service) => {
      const title = getServiceTitle(service, language);
      const description = getServiceDescription(service, language);
      const category = service.category || "";
      return normalise(`${title} ${description} ${category}`).includes(term);
    });
  }, [services, search, language]);

  const paidSelections = React.useMemo(
    () =>
      Object.values(selections).filter((item) => item.service_type === "PAID"),
    [selections],
  );

  const nonPaidSelections = React.useMemo(
    () =>
      Object.values(selections).filter(
        (item) => item.service_type === "NON_PAID",
      ),
    [selections],
  );

  function getSelection(service) {
    return (
      selections[service.code] || createDefaultSelection(service, language)
    );
  }

  function setServiceType(service, serviceType) {
    setSelections((prev) => {
      const current =
        prev[service.code] || createDefaultSelection(service, language);

      if (current.service_type === serviceType) {
        const next = { ...prev };
        delete next[service.code];
        return next;
      }

      return {
        ...prev,
        [service.code]: {
          ...current,
          label: getServiceTitle(service, language),
          description: getServiceDescription(service, language),
          service_type: serviceType,
          subsidy_status:
            serviceType === "PAID"
              ? current.subsidy_status || "NO"
              : "NOT_APPLICABLE",
          approximate_cost_text:
            serviceType === "PAID" ? current.approximate_cost_text : "",
          subsidy_information_url:
            serviceType === "PAID" ? current.subsidy_information_url : "",
          subsidy_information_label:
            serviceType === "PAID" ? current.subsidy_information_label : "",
          subsidy_notes: serviceType === "PAID" ? current.subsidy_notes : "",
        },
      };
    });

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[service.code];
      return next;
    });
  }

  function updateSelectionField(serviceCode, field, value) {
    setSelections((prev) => {
      const current = prev[serviceCode];

      if (!current) return prev;

      return {
        ...prev,
        [serviceCode]: {
          ...current,
          subsidy_status:
            field === "subsidy_status" ? value : current.subsidy_status,
          [field]: value,
        },
      };
    });

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[serviceCode];
      return next;
    });
  }

  function selectAllAs(type) {
    setSelections((prev) => {
      const next = { ...prev };

      filteredServices.forEach((service) => {
        const current =
          next[service.code] || createDefaultSelection(service, language);

        next[service.code] = {
          ...current,
          label: getServiceTitle(service, language),
          description: getServiceDescription(service, language),
          service_type: type,
          subsidy_status:
            type === "PAID" ? current.subsidy_status || "NO" : "NOT_APPLICABLE",
          approximate_cost_text:
            type === "PAID" ? current.approximate_cost_text : "",
          subsidy_information_url:
            type === "PAID" ? current.subsidy_information_url : "",
          subsidy_information_label:
            type === "PAID" ? current.subsidy_information_label : "",
          subsidy_notes: type === "PAID" ? current.subsidy_notes : "",
        };
      });

      return next;
    });
  }

  function clearType(type) {
    setSelections((prev) => {
      const next = { ...prev };

      Object.entries(next).forEach(([code, item]) => {
        if (item.service_type === type) {
          delete next[code];
        }
      });

      return next;
    });
  }

  function validate() {
    const errors = {};
    const selected = Object.values(selections).filter(
      (item) => item.service_type,
    );

    if (!selected.length) {
      errors.general = t("validation.oneServiceRequired");
    }

    selected.forEach((item) => {
      if (item.service_type === "PAID" && !item.approximate_cost_text.trim()) {
        errors[item.service_code] = t("validation.costRequired");
      }

      if (
        item.service_type === "PAID" &&
        item.subsidy_status === "YES" &&
        item.subsidy_information_url &&
        !/^https?:\/\/.+/i.test(item.subsidy_information_url)
      ) {
        errors[item.service_code] = t("validation.validUrl");
      }
    });

    return errors;
  }

  function submit(e) {
    e.preventDefault();

    const errors = validate();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setErr(errors.general || t("messages.correctHighlighted"));
      return;
    }

    const companyDetails = localStorage.getItem(COMPANY_DETAILS_STORAGE_KEY);
    if (!companyDetails) {
      setErr(t("messages.companyDetailsMissing"));
      return;
    }

    setErr("");
    navigate("/register/oss/review-submit");
  }

  function renderServiceCard(service, type) {
    const selected = getSelection(service);
    const isChecked = selected.service_type === type;
    const title = getServiceTitle(service, language);
    const description = getServiceDescription(service, language);
    const hasError = fieldErrors[service.code];

    return (
      <div
        key={`${type}-${service.code}`}
        className={
          isChecked ? "oss-services-item selected" : "oss-services-item"
        }
      >
        <label className="oss-services-check-row">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => setServiceType(service, type)}
          />
          <span>
            <strong>{title}</strong>
            {description && <small>{description}</small>}
          </span>
        </label>

        {type === "PAID" && isChecked && (
          <div className="oss-services-paid-fields">
            <div className="oss-services-mini-grid">
              <div className="oss-services-field">
                <label>
                  {t("fields.subsidised")}<span className="oss-required">*</span>
                </label>
                  
                <select
                  value={selected.subsidy_status}
                  onChange={(e) =>
                    updateSelectionField(
                      service.code,
                      "subsidy_status",
                      e.target.value,
                    )
                  }
                >
                  <option value="NO">{t("common.no")}</option>
                  <option value="YES">{t("common.yes")}</option>
                </select>
              </div>

              <div className="oss-services-field">
                <label>
                  {t("fields.approximateCost")}
                  <span className="oss-required">*</span>
                </label>
                <div className="oss-services-euro-input">
                  <input
                    value={selected.approximate_cost_text}
                    onChange={(e) =>
                      updateSelectionField(
                        service.code,
                        "approximate_cost_text",
                        normaliseEuroInput(e.target.value),
                      )
                    }
                    placeholder={t("placeholders.approximateCost")}
                    className={hasError ? "oss-services-input-error" : ""}
                  />
                  <span>€</span>
                </div>
              </div>
            </div>

            {selected.subsidy_status === "YES" && (
              <div className="oss-services-subsidy-panel">
                <div className="oss-services-field">
                  <label>{t("fields.subsidyInformationUrl")}</label>
                  <input
                    value={selected.subsidy_information_url}
                    onChange={(e) =>
                      updateSelectionField(
                        service.code,
                        "subsidy_information_url",
                        e.target.value,
                      )
                    }
                    placeholder={t("placeholders.subsidyInformationUrl")}
                    className={hasError ? "oss-services-input-error" : ""}
                  />
                </div>

                <div className="oss-services-field">
                  <label>{t("fields.subsidyInformationLabel")}</label>
                  <input
                    value={selected.subsidy_information_label}
                    onChange={(e) =>
                      updateSelectionField(
                        service.code,
                        "subsidy_information_label",
                        e.target.value,
                      )
                    }
                    placeholder={t("placeholders.subsidyInformationLabel")}
                  />
                </div>

                <div className="oss-services-field oss-services-field-full">
                  <label>{t("fields.subsidyNotes")}</label>
                  <textarea
                    value={selected.subsidy_notes}
                    onChange={(e) =>
                      updateSelectionField(
                        service.code,
                        "subsidy_notes",
                        e.target.value,
                      )
                    }
                    placeholder={t("placeholders.subsidyNotes")}
                    rows="2"
                  />
                </div>
              </div>
            )}

            {hasError && (
              <span className="oss-services-field-error">{hasError}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="oss-services-page">
      <section className="oss-services-hero">
        <p className="oss-services-kicker">{t("hero.kicker")}</p>
        <h1>{t("hero.title")}</h1>
        <p>{t("hero.subtitle")}</p>
      </section>

      <form className="oss-services-card" onSubmit={submit}>
        <div className="oss-services-search">
          <Search size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("placeholders.search")}
          />
          {search && (
            <button type="button" onClick={() => setSearch("")}>
              <X size={16} />
            </button>
          )}
        </div>

        {loading && (
          <div className="oss-services-status">{t("messages.loading")}</div>
        )}

        {err && <div className="oss-services-message error">{err}</div>}

        {!loading && !err && (
          <>
            <div className="oss-services-summary-row">
              <div>
                <CheckCircle2 size={16} />
                <span>
                  {t("summary.selected", {
                    count: paidSelections.length + nonPaidSelections.length,
                  })}
                </span>
              </div>
              <div>
                <Euro size={16} />
                <span>
                  {t("summary.paid", { count: paidSelections.length })}
                </span>
              </div>
              <div>
                <Gift size={16} />
                <span>
                  {t("summary.nonPaid", { count: nonPaidSelections.length })}
                </span>
              </div>
            </div>

            <div className="oss-services-columns">
              <section className="oss-services-column">
                <div className="oss-services-column-header">
                  <div>
                    <h2>{t("columns.paid.title")}</h2>
                    <p>{t("columns.paid.subtitle")}</p>
                  </div>

                  <div className="oss-services-actions">
                    <button type="button" onClick={() => clearType("PAID")}>
                      {t("buttons.clear")}
                    </button>
                    <button type="button" onClick={() => selectAllAs("PAID")}>
                      {t("buttons.selectAll")}
                    </button>
                  </div>
                </div>

                <div className="oss-services-list">
                  {filteredServices.map((service) =>
                    renderServiceCard(service, "PAID"),
                  )}
                </div>
              </section>

              <section className="oss-services-column">
                <div className="oss-services-column-header">
                  <div>
                    <h2>{t("columns.nonPaid.title")}</h2>
                    <p>{t("columns.nonPaid.subtitle")}</p>
                  </div>

                  <div className="oss-services-actions">
                    <button type="button" onClick={() => clearType("NON_PAID")}>
                      {t("buttons.clear")}
                    </button>
                    <button
                      type="button"
                      onClick={() => selectAllAs("NON_PAID")}
                    >
                      {t("buttons.selectAll")}
                    </button>
                  </div>
                </div>

                <div className="oss-services-list">
                  {filteredServices.map((service) =>
                    renderServiceCard(service, "NON_PAID"),
                  )}
                </div>
              </section>
            </div>

            {filteredServices.length === 0 && (
              <div className="oss-services-empty">
                {t("messages.noServicesFound")}
              </div>
            )}

            <div className="oss-services-footer">
              <p>{t("footer.help")}</p>
              <button type="submit">{t("buttons.continue")}</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
